import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  Database, Download, Upload, Trash2, RefreshCw, Plus, HardDrive,
  FileJson, Calendar, CheckCircle, AlertTriangle, Info, Shield
} from 'lucide-react';

const BackupsPage = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [info, setInfo] = useState(null);
  
  // Modal crear backup
  const [createOpen, setCreateOpen] = useState(false);
  const [incluirTransacciones, setIncluirTransacciones] = useState(true);
  const [incluirHistorial, setIncluirHistorial] = useState(false);
  const [nota, setNota] = useState('');
  
  // Modal restaurar
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [soloCatalogos, setSoloCatalogos] = useState(false);
  const [restoring, setRestoring] = useState(false);
  
  // Confirmar eliminar
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const cargarBackups = useCallback(async () => {
    setLoading(true);
    try {
      const [backupsRes, infoRes] = await Promise.all([
        api.get('/backups'),
        api.get('/backups/info')
      ]);
      setBackups(backupsRes.data.backups || []);
      setInfo(infoRes.data);
    } catch (error) {
      toast.error('Error al cargar backups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarBackups(); }, [cargarBackups]);

  const crearBackup = async () => {
    setCreating(true);
    try {
      const res = await api.post('/backups/crear', null, {
        params: {
          incluir_transacciones: incluirTransacciones,
          incluir_historial: incluirHistorial,
          nota: nota || null
        }
      });
      toast.success(res.data.mensaje);
      setCreateOpen(false);
      setNota('');
      cargarBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear backup');
    } finally {
      setCreating(false);
    }
  };

  const descargarBackup = async (filename) => {
    try {
      const response = await api.get(`/backups/descargar/${filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Backup descargado');
    } catch (error) {
      toast.error('Error al descargar');
    }
  };

  const abrirRestaurar = async (backup) => {
    setSelectedBackup(backup);
    setRestorePreview(null);
    setSoloCatalogos(false);
    setRestoreOpen(true);
    
    try {
      const res = await api.post(`/backups/restaurar/${backup.filename}`, null, {
        params: { confirmar: false }
      });
      setRestorePreview(res.data);
    } catch (error) {
      toast.error('Error al cargar preview');
    }
  };

  const ejecutarRestauracion = async () => {
    if (!selectedBackup) return;
    setRestoring(true);
    try {
      const res = await api.post(`/backups/restaurar/${selectedBackup.filename}`, null, {
        params: { confirmar: true, solo_catalogos: soloCatalogos }
      });
      toast.success(res.data.mensaje);
      setRestoreOpen(false);
      cargarBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    } finally {
      setRestoring(false);
    }
  };

  const eliminarBackup = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/backups/${deleteConfirm.filename}`);
      toast.success('Backup eliminado');
      setDeleteConfirm(null);
      cargarBackups();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatDate = (dateStr) => {
    // El backend ya envía la hora en zona Lima, solo formatear
    const date = new Date(dateStr);
    return date.toLocaleString('es-PE', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  return (
    <div className="space-y-6" data-testid="backups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">Copias de Seguridad</h1>
            <p className="text-sm text-gray-500">Respaldo y restauración de datos</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={cargarBackups} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Crear Backup
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Backups</p>
                <p className="text-2xl font-bold">{info?.backups_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <HardDrive className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Espacio Usado</p>
                <p className="text-2xl font-bold">{info?.espacio_usado_mb || 0} MB</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Último Backup</p>
                <p className="text-lg font-semibold">
                  {backups[0] ? formatDate(backups[0].fecha) : 'Ninguno'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de backups */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileJson className="w-5 h-5" />
            Backups Disponibles
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No hay backups</p>
              <p className="text-sm text-gray-400">Crea tu primer backup para proteger tus datos</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Archivo</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Tablas</TableHead>
                  <TableHead className="text-right">Registros</TableHead>
                  <TableHead className="text-right">Tamaño</TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backups.map((backup, idx) => (
                  <TableRow key={backup.filename}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileJson className="w-4 h-4 text-blue-500" />
                        <span className="font-mono text-sm">{backup.filename}</span>
                        {idx === 0 && <Badge className="bg-green-100 text-green-700 text-xs">Más reciente</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(backup.fecha)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{backup.tablas}</TableCell>
                    <TableCell className="text-right font-mono">{backup.registros.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{backup.size_kb} KB</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => descargarBackup(backup.filename)} title="Descargar">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => abrirRestaurar(backup)} title="Restaurar" className="text-amber-600">
                          <Upload className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(backup)} title="Eliminar" className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear Backup */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Crear Nuevo Backup
            </DialogTitle>
            <DialogDescription>
              Selecciona qué datos incluir en el respaldo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Configuración y Catálogos</p>
                <p className="text-sm text-gray-500">Empresas, categorías, usuarios, proveedores, etc.</p>
              </div>
              <Badge className="bg-green-100 text-green-700">Siempre incluido</Badge>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Transacciones</p>
                <p className="text-sm text-gray-500">Compras, ventas, pagos, presupuestos</p>
              </div>
              <Switch checked={incluirTransacciones} onCheckedChange={setIncluirTransacciones} />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Historial de Actividad</p>
                <p className="text-sm text-gray-500">Registro de acciones de usuarios</p>
              </div>
              <Switch checked={incluirHistorial} onCheckedChange={setIncluirHistorial} />
            </div>

            <div>
              <Label>Nota (opcional)</Label>
              <Input 
                value={nota} 
                onChange={(e) => setNota(e.target.value)}
                placeholder="Ej: Backup antes de actualización"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={crearBackup} disabled={creating} className="bg-blue-600 hover:bg-blue-700">
              {creating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              {creating ? 'Creando...' : 'Crear Backup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Restaurar */}
      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Restaurar Backup
            </DialogTitle>
            <DialogDescription>
              {selectedBackup?.filename}
            </DialogDescription>
          </DialogHeader>

          {restorePreview && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Advertencia</p>
                    <p className="text-sm text-amber-700">{restorePreview.advertencia}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="font-medium mb-2">Tablas a restaurar:</p>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2">
                  {restorePreview.tablas?.map((t, idx) => (
                    <div key={idx} className="flex justify-between py-1 text-sm">
                      <span className="font-mono">{t.tabla}</span>
                      <Badge variant="outline">{t.registros} registros</Badge>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Solo catálogos</p>
                  <p className="text-sm text-gray-500">No restaurar transacciones</p>
                </div>
                <Switch checked={soloCatalogos} onCheckedChange={setSoloCatalogos} />
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setRestoreOpen(false)}>Cancelar</Button>
            <Button onClick={ejecutarRestauracion} disabled={restoring} className="bg-amber-600 hover:bg-amber-700">
              {restoring ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {restoring ? 'Restaurando...' : 'Restaurar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminar */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el archivo "{deleteConfirm?.filename}". Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarBackup} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupsPage;
